import { Button, Loader } from "@/components/ui";
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMicroSiteContext } from "@/contexts/MicroSiteContext";
import { useState } from "react";
import FileUpload from '@/components/ui/FileUpload';
import { useFileUpload } from '@/lib/api/hooks/file-upload';
import UploadIcon from "@/assets/icons/UploadIcon";
import { showToast } from '@/lib/toast';
import { TemplateDataSchemaType } from "@/pages/articles-pages/programmes/MultiStepForm/MicroSite/Templates/templateDataSchema";
import { Heading, Label, Paragraph } from "@/components/ui/Typography";
import Input from "@/components/ui/Input/Input";
import CloseIcon from "@/assets/icons/CloseIcon";

const FormDataSchema = z.object({
  websiteLogoUrl: z.string().min(1, 'Logo is required'),
  websiteColorCode: z.string().min(1, 'Primary color is required'),
  textColorCode: z.string().min(1, 'Text color is required'),
});


const BrandingForm = ({handleCancel}:{handleCancel: () => void }) => {

  const [isUploading, setIsUploading] = useState(false);
      const { templatePayload:templateData, updateTemplatePayload } = useMicroSiteContext();
  
      // const [previewUrl, setPreviewUrl] = useState<string | null>(null);
        const fileUploadMutation = useFileUpload();
    
    const {
            control,
            handleSubmit,
            setValue,
            formState: { errors },
            reset
          } = useForm({
            resolver: zodResolver(FormDataSchema),
            defaultValues: {
              websiteLogoUrl: templateData.branding.websiteLogoUrl ?? '',
              websiteColorCode: templateData.branding.websiteColorCode ?? '',
              textColorCode: templateData.branding.textColorCode ?? '',
            },
            mode: 'all'
          });

const handleSave = (formData: TemplateDataSchemaType["branding"]) => {
        // setShowForm?.(false);
        const updatedPayload = {
          ...templateData,
          branding: {
            ...templateData.branding,
            websiteLogoUrl: formData.websiteLogoUrl,
            websiteColorCode: formData.websiteColorCode,
            textColorCode: formData.textColorCode
          }
        };
        updateTemplatePayload(updatedPayload);
        handleCancel();
      };
  
          const handleFileChange = async(files: File[] | null) => {
          if (files && files.length > 0) {
            setIsUploading(true);
            const file = files[0];
            try {
              const response = await fileUploadMutation.mutateAsync({
                file: file
              });
              setValue('websiteLogoUrl', response?.data?.fileUrl, { shouldValidate: true });
              // setPreviewUrl(response?.data?.fileUrl);
            } catch (error) {
              console.error('File upload failed:', error);
              showToast.error('File upload failed. Please try again.');
            } finally {
              setIsUploading(false);
            }
          } else {
            // setPreviewUrl(null);
            setValue('websiteLogoUrl', "", { shouldValidate: true });
          }
        };
    return (
      <div className="flex flex-col relative gap-4 w-full p-4 bg-[#F2F2F2]">
        <CloseIcon strokeWidth={2} stroke="#000"  className="absolute z-50 top-4 right-4 cursor-pointer" onClick={handleCancel} />
        <div>
          <Heading level={4}>Branding</Heading>
          <Paragraph>Change logo & colors</Paragraph>
        </div>
        <form onSubmit={handleSubmit(handleSave)} className="flex flex-col gap-4">
             <div className="relative mb-6">
              {
              isUploading && <div className="absolute inset-0 bg-white/70 z-10 flex items-center justify-center">
                <Loader />  
              </div> 
            }
            <Controller
          name="websiteLogoUrl"
          control={control}
          render={({ field }) => (
            <FileUpload
            {...field}
              onChange={(files) => {
                handleFileChange(files);
                field.onChange(files ? URL.createObjectURL(files[0]) : '');
              }}
              label=""
              placeholder=""
              $maxSizeMB={5}
              error={errors.websiteLogoUrl?.message}
              multiple={false}
              allowedFileTypes={['image/*']}
              containerClassName="border border-[#E5E5E5] bg-white"
              $dataId="inline-file-upload"
            >
              <div className="flex flex-col md:flex-row items-center justify-center gap-4">
               <div className="flex-1">
                 <Paragraph className="font-semibold text-primary">
                  Website logo
                </Paragraph>
                <Paragraph className="!text-sm text-text-secondary">
                  Image files (PNG, JPEG, SVG, GIF) with max size of 1mb
                </Paragraph>
               </div>
               
                <div
                  className="bg-duskySky-100 border border-dashed border-[#E5E5E5] flex flex-col items-center justify-center hover:bg-duskySky-100  hover:text-[#000] px-4 py-2 rounded-md font-semibold text-center"
                >
                   <UploadIcon width={28}  />
                   <Label>Add Image</Label>
                </div>
              </div>
            </FileUpload>
          )}
        />
          </div>

           <Controller
          name="websiteColorCode"
          control={control}
          render={({ field }) => (
            <Input
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              placeholder="Website Color"
              label="Website Color"
              type="color"
              error={errors.websiteColorCode?.message}
              $dataId="websiteColorCode-input"
              required
              $marginbottom="0"
            />
          )}
        />
   
           <Controller
          name="textColorCode"
          control={control}
          render={({ field }) => (
            <Input
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              placeholder="Text Color"
              label="Text Color"
              type="color"
              error={errors.textColorCode?.message}
              $dataId="textColorCode-input"
              required
              $marginbottom="0"
            />
          )}
        />

        
    

            <div className="flex gap-2 justify-end mt-4">
            <Button
              variant="secondary"
              className=""
              onClick={() => {
                handleCancel();
                reset();
              }}
            >
              Cancel
            </Button>
            <Button
              className=""
              onClick={handleSubmit(handleSave)}
            >
              Save
            </Button>
          </div>
        </form>
      </div>
    )
  };

  export default BrandingForm;